import React, { useState, useEffect, useRef } from "react"
import { createRoot } from "react-dom/client"
import { HtmlEditorCommander } from "aldoo-ra/Commander"
import modalStack from "aldoo-ra/Modal/ModalStack"

const { runCommand } = HtmlEditorCommander
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Tooltip from "aldoo-ra/Tooltip"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Input from "aldoo-ra/Input"

const HorizontalImageOptionsMenu = ({ selectedImage, onClose, onImageUpdated, variables = {} }) => {
  const [captionInputVisible, setCaptionInputVisible] = useState(false)
  const [captionExists, setCaptionExists] = useState(false)
  const [captionText, setCaptionText] = useState("")
  
  // Ensure no undefined variables
  const captionVisible = captionExists
  const [altTextPanelOpen, setAltTextPanelOpen] = useState(false)
  const [altText, setAltText] = useState("")
  const [selectedWrapAlign, setSelectedWrapAlign] = useState([])
  const [selectedBreakAlign, setSelectedBreakAlign] = useState([])
  const [selectedSize, setSelectedSize] = useState([])
  const [selectedObjectFit, setSelectedObjectFit] = useState([])
  
  const captionInputRef = useRef(null)

  // Dropdown options
  const wrapAlignOptions = [
    { value: "Left", label: "Left" },
    { value: "Right", label: "Right" }
  ]

  const breakAlignOptions = [
    { value: "Left", label: "Left" },
    { value: "Center", label: "Center" },
    { value: "Right", label: "Right" }
  ]

  const sizeOptions = [
    { value: "Original", label: "Original (100%)" },
    { value: "75%", label: "75%" },
    { value: "50%", label: "50%" },
    { value: "25%", label: "25%" }
  ]

  const objectFitOptions = [
    { value: "Fill", label: "Fill" },
    { value: "Contain", label: "Contain" },
    { value: "Cover", label: "Cover" },
    { value: "None", label: "None" },
    { value: "Scale-down", label: "Scale-down" }
  ]

  // Initialize states based on current image
  useEffect(() => {
    if (!selectedImage) return
    
    // Check for existing caption with multiple fallback strategies
    let captionElement = null
    let captionText = ""
    
    // Strategy 1: Look for direct sibling with image-caption class
    const nextElement = selectedImage.nextElementSibling
    if (nextElement && nextElement.classList.contains("image-caption")) {
      captionElement = nextElement
    }
    
    // Strategy 2: Look for any element with image-caption class near this image
    if (!captionElement) {
      const parent = selectedImage.parentElement
      if (parent) {
        // Look for caption in same container
        const nearbyCaption = parent.querySelector(".image-caption")
        if (nearbyCaption) {
          // Check if this caption is associated with our image
          const prevImg = nearbyCaption.previousElementSibling
          if (prevImg === selectedImage) {
            captionElement = nearbyCaption
          }
        }
      }
    }
    
    // Strategy 3: Look for figcaption if image is in a figure
    if (!captionElement) {
      const figure = selectedImage.closest("figure")
      if (figure) {
        const figcaption = figure.querySelector("figcaption")
        if (figcaption) {
          captionElement = figcaption
        }
      }
    }
    
    // Strategy 4: Look for data-caption attribute on the image itself
    if (!captionElement && selectedImage.hasAttribute("data-caption")) {
      captionText = selectedImage.getAttribute("data-caption")
    }
    
    if (captionElement) {
      captionText = captionElement.textContent || ""
      // Check if caption has content and is visible
      const hasContent = captionText.trim().length > 0
      const isVisible = captionElement.style.display !== "none"
      setCaptionExists(hasContent && isVisible)
      setCaptionInputVisible(false)
    } else if (captionText) {
      setCaptionExists(true)
      setCaptionInputVisible(false)
    } else {
      setCaptionExists(false)
      setCaptionInputVisible(false)
    }
    
    setCaptionText(captionText)
    
    // Set alt text
    setAltText(selectedImage.alt || "")
    
    // Detect current alignment/wrap state from figure container or image
    const figureContainer = selectedImage.closest("figure")
    const targetElement = figureContainer || selectedImage
    
    const float = targetElement.style.float
    const margin = targetElement.style.margin
    const display = targetElement.style.display
    
    // Check if it's using wrap text (float-based alignment)
    if (float === "left") {
      setSelectedWrapAlign([{ value: "Left", label: "Left" }])
      setSelectedBreakAlign([])
    } else if (float === "right") {
      setSelectedWrapAlign([{ value: "Right", label: "Right" }])
      setSelectedBreakAlign([])
    } 
    // Check if it's using break text (block-based alignment)
    else if ((display === "block" && float === "none") || (display === "block" && !float)) {
      setSelectedWrapAlign([])
      
      if (margin.includes("auto")) {
        if (margin.includes("1rem 0 1rem auto")) {
          setSelectedBreakAlign([{ value: "Right", label: "Right" }])
        } else if (margin.includes("1rem auto")) {
          setSelectedBreakAlign([{ value: "Center", label: "Center" }])
        } else {
          setSelectedBreakAlign([{ value: "Left", label: "Left" }])
        }
      } else if (margin.includes("1rem 0 1rem 0")) {
        setSelectedBreakAlign([{ value: "Left", label: "Left" }])
      } else {
        setSelectedBreakAlign([])
      }
    } 
    // Default state - no alignment applied
    else {
      setSelectedWrapAlign([])
      setSelectedBreakAlign([])
    }
    
    // Detect current size from figure container or image
    const width = figureContainer ? (figureContainer.style.width || selectedImage.style.width) : selectedImage.style.width
    if (width === "100%") {
      setSelectedSize([{ value: "Original", label: "Original (100%)" }])
    } else if (width === "75%") {
      setSelectedSize([{ value: "75%", label: "75%" }])
    } else if (width === "50%") {
      setSelectedSize([{ value: "50%", label: "50%" }])
    } else if (width === "25%") {
      setSelectedSize([{ value: "25%", label: "25%" }])
    } else {
      setSelectedSize([])
    }
    
    // Detect object-fit
    const objectFit = selectedImage.style.objectFit
    if (objectFit) {
      const fitValue = objectFit.charAt(0).toUpperCase() + objectFit.slice(1)
      const fitOption = objectFitOptions.find(option => option.value === fitValue)
      if (fitOption) {
        setSelectedObjectFit([fitOption])
      } else {
        setSelectedObjectFit([])
      }
    } else {
      setSelectedObjectFit([])
    }
  }, [selectedImage])

  // Focus caption input when it becomes visible
  useEffect(() => {
    if (captionInputVisible && captionInputRef.current) {
      captionInputRef.current.focus()
    }
  }, [captionInputVisible])

  // Cleanup effect - close panels when component unmounts
  useEffect(() => {
    return () => {
      setCaptionInputVisible(false)
      setAltTextPanelOpen(false)
      
      // Force hide any visible tooltips by triggering mouse leave on all tooltip triggers
      const tooltipTriggers = document.querySelectorAll('.ae-horizontal-options-menu button, .ae-horizontal-options-menu [onMouseEnter]')
      tooltipTriggers.forEach(trigger => {
        trigger.dispatchEvent(new MouseEvent('mouseleave', {
          view: window,
          bubbles: true,
          cancelable: true
        }))
      })
    }
  }, [])


  const handleCaptionToggle = () => {
    if (captionInputVisible) {
      // If input is visible, cancel/hide it
      setCaptionInputVisible(false)
      setCaptionText(captionExists ? captionText : "")
    } else if (captionExists) {
      // When caption exists, show input with current text for editing
      setCaptionInputVisible(true)
    } else {
      // Show caption input when clicking "Add Caption"
      setCaptionInputVisible(true)
    }
  }

  const findImageCaption = (image) => {
    // Strategy 1: Look for caption with matching data-caption-for attribute
    const imageId = image.getAttribute('data-image-id')
    if (imageId) {
      const associatedCaption = document.querySelector(`[data-caption-for="${imageId}"]`)
      if (associatedCaption) {
        return associatedCaption
      }
    }
    
    // Strategy 2: Look for caption with matching data-image-id (old system)
    if (image.id) {
      const associatedCaption = document.querySelector(`[data-image-id="${image.id}"]`)
      if (associatedCaption && associatedCaption.classList.contains("image-caption")) {
        return associatedCaption
      }
    }
    
    // Strategy 3: Direct sibling with image-caption class
    const nextElement = image.nextElementSibling
    if (nextElement && nextElement.classList.contains("image-caption")) {
      return nextElement
    }
    
    // Strategy 4: Look in parent for associated caption
    const parent = image.parentElement
    if (parent) {
      const nearbyCaption = parent.querySelector(".image-caption")
      if (nearbyCaption && nearbyCaption.previousElementSibling === image) {
        return nearbyCaption
      }
    }
    
    // Strategy 5: Look for figcaption if image is in figure
    const figure = image.closest("figure")
    if (figure) {
      return figure.querySelector("figcaption")
    }
    
    return null
  }

  const handleCaptionTurnOff = () => {
    // Find caption using robust detection
    const captionElement = findImageCaption(selectedImage)
    if (captionElement) {
      // Instead of removing, just clear the text content to preserve layout
      captionElement.textContent = ""
      captionElement.style.display = "none" // Hide empty caption visually
    }
    
    // Clear data-caption attribute if it exists
    if (selectedImage.hasAttribute("data-caption")) {
      selectedImage.removeAttribute("data-caption")
    }
    
    // Update state immediately 
    setCaptionInputVisible(false)
    setCaptionExists(false)
    setCaptionText("")
    
    // Force a small delay to ensure DOM changes are complete before callback
    setTimeout(() => {
      if (onImageUpdated) onImageUpdated(selectedImage)
    }, 0)
  }

  const applyCaptionAndHide = () => {
    if (!captionText.trim()) {
      setCaptionInputVisible(false)
      return
    }

    // Use robust detection to find existing caption
    const existingCaption = findImageCaption(selectedImage)
    
    if (existingCaption) {
      // Update existing caption and make sure it's visible
      existingCaption.textContent = captionText
      existingCaption.style.display = "" // Show caption if it was hidden
    } else {
      // Check if image is already in a figure container
      let figureContainer = selectedImage.closest("figure")
      
      if (!figureContainer) {
        // Create a figure container to wrap image and caption together
        figureContainer = document.createElement("figure")
        figureContainer.className = "image-figure"
        
        // Transfer image styles to figure container to maintain alignment
        const imageStyles = {
          float: selectedImage.style.float,
          display: selectedImage.style.display,
          margin: selectedImage.style.margin,
          marginLeft: selectedImage.style.marginLeft,
          marginRight: selectedImage.style.marginRight,
          marginTop: selectedImage.style.marginTop,
          marginBottom: selectedImage.style.marginBottom,
          width: selectedImage.style.width,
          maxWidth: selectedImage.style.maxWidth
        }
        
        // Apply styles to figure
        Object.entries(imageStyles).forEach(([property, value]) => {
          if (value) {
            figureContainer.style[property] = value
          }
        })
        
        // Clear styles from image since figure now handles layout
        selectedImage.style.float = ''
        selectedImage.style.display = ''
        selectedImage.style.margin = ''
        selectedImage.style.marginLeft = ''
        selectedImage.style.marginRight = ''
        selectedImage.style.marginTop = ''
        selectedImage.style.marginBottom = ''
        // Keep width on image for proper sizing
        
        // Replace image with figure container
        selectedImage.parentNode.insertBefore(figureContainer, selectedImage)
        figureContainer.appendChild(selectedImage)
      }
      
      // Create caption element with simple styling
      const captionElement = document.createElement("figcaption")
      captionElement.textContent = captionText
      captionElement.className = "image-caption"
      captionElement.style.textAlign = "center"
      captionElement.style.fontSize = "14px"
      captionElement.style.color = "#666666"
      captionElement.style.marginTop = "8px"
      captionElement.style.fontStyle = "italic"
      
      // Add data attribute for reliable association
      let imageId = selectedImage.getAttribute('data-image-id')
      if (!imageId) {
        imageId = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        selectedImage.setAttribute('data-image-id', imageId)
      }
      captionElement.setAttribute("data-caption-for", imageId)
      
      // Append caption to figure container
      figureContainer.appendChild(captionElement)
    }

    // Also store caption as data attribute for additional reliability
    selectedImage.setAttribute("data-caption", captionText)

    if (onImageUpdated) onImageUpdated(selectedImage)
    setCaptionInputVisible(false)
    setCaptionExists(true) // Caption now exists in DOM
  }

  const handleCaptionBlur = () => {
    applyCaptionAndHide()
  }

  const handleCaptionKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      applyCaptionAndHide()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setCaptionInputVisible(false)
      setCaptionText("")
    }
  }

  const clearAllAlignment = () => {
    // Check if image is in a figure container
    const figureContainer = selectedImage.closest("figure")
    const targetElement = figureContainer || selectedImage
    
    // Clear all alignment styles from the container (figure or image)
    targetElement.style.float = ""
    targetElement.style.display = ""
    targetElement.style.margin = ""
    targetElement.style.marginLeft = ""
    targetElement.style.marginRight = ""
    targetElement.style.marginTop = ""
    targetElement.style.marginBottom = ""
    
    // If we have a figure, make sure image doesn't have conflicting styles
    if (figureContainer) {
      selectedImage.style.float = ""
      selectedImage.style.display = ""
      selectedImage.style.margin = ""
      selectedImage.style.marginLeft = ""
      selectedImage.style.marginRight = ""
      selectedImage.style.marginTop = ""
      selectedImage.style.marginBottom = ""
    }
  }

  const handleWrapAlign = (values) => {
    setSelectedWrapAlign(values)
    setSelectedBreakAlign([]) // Reset break text when wrap is selected
    
    if (!values || values.length === 0) {
      clearAllAlignment()
      if (onImageUpdated) onImageUpdated(selectedImage)
      return
    }
    
    const alignment = values[0].value
    
    // Clear all existing alignment styles first
    clearAllAlignment()
    
    // Check if image is in a figure container
    const figureContainer = selectedImage.closest("figure")
    const targetElement = figureContainer || selectedImage
    
    // Apply wrap text styles to the container (figure or image)
    switch (alignment) {
      case "Left":
        targetElement.style.float = "left"
        targetElement.style.marginRight = "1rem"
        targetElement.style.marginLeft = "0"
        targetElement.style.marginBottom = "0.5rem"
        break
      case "Right":
        targetElement.style.float = "right"
        targetElement.style.marginLeft = "1rem"
        targetElement.style.marginRight = "0"
        targetElement.style.marginBottom = "0.5rem"
        break
    }

    if (onImageUpdated) onImageUpdated(selectedImage)
  }

  const handleBreakAlign = (values) => {
    setSelectedBreakAlign(values)
    setSelectedWrapAlign([]) // Reset wrap text when break is selected
    
    if (!values || values.length === 0) {
      clearAllAlignment()
      if (onImageUpdated) onImageUpdated(selectedImage)
      return
    }
    
    const alignment = values[0].value
    
    // Clear all existing alignment styles first
    clearAllAlignment()
    
    // Check if image is in a figure container
    const figureContainer = selectedImage.closest("figure")
    const targetElement = figureContainer || selectedImage
    
    // Apply break text styles to the container (figure or image)
    switch (alignment) {
      case "Left":
        targetElement.style.display = "block"
        targetElement.style.float = "none"
        targetElement.style.margin = "1rem 0 1rem 0"
        break
      case "Center":
        targetElement.style.display = "block"
        targetElement.style.float = "none"
        targetElement.style.margin = "1rem auto"
        break
      case "Right":
        targetElement.style.display = "block"
        targetElement.style.float = "none"
        targetElement.style.margin = "1rem 0 1rem auto"
        break
    }

    if (onImageUpdated) onImageUpdated(selectedImage)
  }

  const handleSizeChange = (values) => {
    setSelectedSize(values)
    
    if (!values || values.length === 0) {
      if (onImageUpdated) onImageUpdated(selectedImage)
      return
    }
    
    const sizeValue = values[0].value
    let size = sizeValue
    
    // Convert "Original" to "100%"
    if (sizeValue === "Original") {
      size = "100%"
    }
    
    
    // Use the image plugin's setSize logic
    runCommand("image.size", { selectedImage, size })

    if (onImageUpdated) onImageUpdated(selectedImage)
  }

  const handleObjectFitChange = (values) => {
    setSelectedObjectFit(values)
    
    if (!values || values.length === 0) {
      if (onImageUpdated) onImageUpdated(selectedImage)
      return
    }
    
    const fit = values[0].value
    selectedImage.style.objectFit = fit.toLowerCase()
    if (onImageUpdated) onImageUpdated(selectedImage)
  }

  const handleAltTextSave = () => {
    selectedImage.alt = altText
    setAltTextPanelOpen(false)
    if (onImageUpdated) onImageUpdated(selectedImage)
  }

  const handleReplaceImage = () => {
    runCommand("image.replace", {
      selectedImage,
      onReplaceComplete: (img) => {
        if (onImageUpdated) onImageUpdated(img || selectedImage)
      },
    })
    onClose()
  }

  const handleDeleteImage = () => {
    // Remove any associated caption before deleting the image
    const captionElement = findImageCaption(selectedImage)
    if (captionElement) {
      captionElement.remove()
    }
    
    // If image is in a figure container, remove the entire figure
    const figureContainer = selectedImage.closest("figure")
    if (figureContainer) {
      // Check if figure has any other content besides the image and caption
      const figureChildren = Array.from(figureContainer.children)
      const hasOtherContent = figureChildren.some(child => 
        child !== selectedImage && 
        !child.matches('figcaption') &&
        child.textContent.trim() !== ''
      )
      
      if (!hasOtherContent) {
        // Safe to remove entire figure
        figureContainer.remove()
        onClose()
        if (onImageUpdated) onImageUpdated(null)
        return
      }
    }
    
    // Fallback to command-based deletion
    runCommand("image.delete", {
      selectedImage,
      onDeleteComplete: () => {
        if (onImageUpdated) onImageUpdated(null)
      },
    })
    onClose()
  }

  const ToolbarButton = ({ onClick, tooltip, active = false, children }) => (
    <Tooltip 
      content={tooltip} 
      variant="dark" 
          >
      <button
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (onClick) {
            onClick(e)
          }
        }}
        onMouseDown={(e) => {
          // Prevent mousedown from being handled by click outside handler
          e.stopPropagation()
        }}
        className={`flex items-center justify-center w-10 h-10 rounded-lg border transition-colors ${
          active 
            ? 'bg-admin_primary text-white border-admin_primary' 
            : 'bg-admin_paper text-admin_text_secondary border-admin_bordered hover:bg-admin_neutral_1'
        }`}
      >
        {children}
      </button>
    </Tooltip>
  )

  return (
    <div className="relative">
      {/* Add CSS to hide dropdown arrows */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .ae-hide-dropdown-arrow svg[data-slot="icon"] {
            display: none !important;
          }
          .ae-hide-dropdown-arrow .w-5.h-5.transition-transform {
            display: none !important;
          }
        `
      }} />

      {/* Horizontal Toolbar */}
      <div className="bg-admin_paper border border-admin_bordered rounded-lg shadow-lg p-2 w-fit">
        <div className="flex flex-col gap-2">
          {/* First Row */}
          <div className="flex items-center gap-2">

          {/* Caption Toggle */}
          <ToolbarButton
            onClick={handleCaptionToggle}
            tooltip={captionInputVisible ? "Cancel Caption" : (captionExists ? "Edit Caption" : "Add Caption")}
            active={captionExists || captionInputVisible}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
          </ToolbarButton>

          {/* Link Edit Button - Only show if image is inside a link */}
          {selectedImage?.closest('a') && (
            <ToolbarButton
              onClick={() => {
                const linkElement = selectedImage.closest('a')
                if (linkElement) {
                  // Close the image menu first
                  onClose()
                  // Then open the link edit dialog
                  runCommand("link.edit", { target: linkElement, variables })
                }
              }}
              tooltip="Edit Link"
              active={false}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </ToolbarButton>
          )}

          {/* View Source Button */}
          <ToolbarButton
            onClick={() => {
              const buttonElement = selectedImage?.closest('.editor-button')
              if (buttonElement) {
                // Image is inside a button - open button source dialog
                onClose()
                runCommand("button.source.edit", { target: buttonElement })
              } else {
                // Standalone image - open image source dialog
                onClose()
                runCommand("image.source.edit", { target: selectedImage })
              }
            }}
            tooltip="View Source"
            active={false}
          >
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </ToolbarButton>

          {/* Wrap Text Dropdown */}
          <DropdownSelect
            variant="inline"
            options={wrapAlignOptions}
            value={selectedWrapAlign}
            onChange={handleWrapAlign}
            placeholder="Wrap Text"
            enableResetButton={false}
            showCheckbox={false}
            className="ae-hide-dropdown-arrow"
            onValueItemRender={(item) => {
              // Always show an icon, even for placeholder
              const iconPath = item ? 
                (item.value === "Left" ? "M4 6h16M4 12h8m-8 6h16" : "M4 6h16M12 12h8M4 18h16") :
                "M4 6h16M4 10h12M4 14h16M4 18h12" // Default wrap text icon
              
              const tooltipText = item ? `Wrap Text: ${item.label}` : "Wrap Text"
              
              return (
                <Tooltip 
                  content={tooltipText} 
                  variant="dark" 
                                  >
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg border border-admin_bordered bg-admin_paper hover:bg-admin_neutral_1 transition-colors">
                    <svg 
                      className="w-5 h-5" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                    </svg>
                  </div>
                </Tooltip>
              )
            }}
            onItemRender={(item) => (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d={item.value === "Left" ? "M4 6h16M4 12h8m-8 6h16" : "M4 6h16M12 12h8M4 18h16"} />
                </svg>
                <span>{item.label}</span>
              </div>
            )}
          />

          {/* Break Text Dropdown */}
          <DropdownSelect
            variant="inline"
            options={breakAlignOptions}
            value={selectedBreakAlign}
            onChange={handleBreakAlign}
            placeholder="Break Text"
            enableResetButton={false}
            showCheckbox={false}
            className="ae-hide-dropdown-arrow"
            onValueItemRender={(item) => {
              // Always show an icon, even for placeholder
              const iconPath = item ? 
                (item.value === "Left" ? "M4 6h16M4 12h16M4 18h7" : 
                 item.value === "Center" ? "M4 6h16M6 12h12M4 18h16" : 
                 "M4 6h16M4 12h16M10 18h10") :
                "M4 6h16M4 12h16M4 18h16" // Default break text icon
              
              const tooltipText = item ? `Break Text: ${item.label}` : "Break Text"
              
              return (
                <Tooltip 
                  content={tooltipText} 
                  variant="dark" 
                                  >
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg border border-admin_bordered bg-admin_paper hover:bg-admin_neutral_1 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                    </svg>
                  </div>
                </Tooltip>
              )
            }}
            onItemRender={(item) => (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d={item.value === "Left" ? "M4 6h16M4 12h16M4 18h7" : 
                          item.value === "Center" ? "M4 6h16M6 12h12M4 18h16" : 
                          "M4 6h16M4 12h16M10 18h10"} />
                </svg>
                <span>{item.label}</span>
              </div>
            )}
          />

          {/* Alt Text */}
          <ToolbarButton
            onClick={() => {
              // Close the horizontal menu and open alt text editor
              onClose()
              // Open alt text editor in the same position
              showAltTextEditor(selectedImage)
            }}
            tooltip="Edit Alt Text"
            active={altText.length > 0}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </ToolbarButton>
          </div>

          {/* Second Row */}
          <div className="flex items-center gap-2">
          {/* Size Dropdown */}
          <DropdownSelect
            variant="inline"
            options={sizeOptions}
            value={selectedSize}
            onChange={handleSizeChange}
            placeholder="Size"
            enableResetButton={false}
            showCheckbox={false}
            className="ae-hide-dropdown-arrow"
            onValueItemRender={(item) => {
              // Always show size icon
              const tooltipText = item ? `Size: ${item.label}` : "Image Size"
              
              return (
                <Tooltip 
                  content={tooltipText} 
                  variant="dark" 
                                  >
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg border border-admin_bordered bg-admin_paper hover:bg-admin_neutral_1 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </div>
                </Tooltip>
              )
            }}
            onItemRender={(item) => (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                <span>{item.label}</span>
              </div>
            )}
          />

          {/* Replace Image */}
          <ToolbarButton
            onClick={handleReplaceImage}
            tooltip="Replace Image"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
          </ToolbarButton>

          {/* Delete Image */}
          <ToolbarButton
            onClick={handleDeleteImage}
            tooltip="Delete Image"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H9a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </ToolbarButton>

          {/* Object Fit Dropdown */}
          <DropdownSelect
            variant="inline"
            options={objectFitOptions}
            value={selectedObjectFit}
            onChange={handleObjectFitChange}
            placeholder="Fit"
            enableResetButton={false}
            showCheckbox={false}
            className="ae-hide-dropdown-arrow"
            onValueItemRender={(item) => {
              // Always show object fit icon
              const tooltipText = item ? `Object Fit: ${item.label}` : "Object Fit"
              
              return (
                <Tooltip 
                  content={tooltipText} 
                  variant="dark" 
                                  >
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg border border-admin_bordered bg-admin_paper hover:bg-admin_neutral_1 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                    </svg>
                  </div>
                </Tooltip>
              )
            }}
            onItemRender={(item) => (
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
                <span>{item.label}</span>
              </div>
            )}
          />
          </div>
        </div>
      </div>

      {/* Caption Input */}
      {captionInputVisible && (
        <div className="mt-2 p-2 bg-admin_paper border border-admin_bordered rounded-lg shadow-sm">
          <div className="flex items-center gap-2">
            <button
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleCaptionTurnOff()
              }}
              className="flex-shrink-0 px-3 py-2 text-sm rounded-md border border-admin_bordered bg-admin_paper text-admin_text_secondary hover:bg-admin_neutral_1 transition-colors"
            >
              Turn Off
            </button>

            <Input
              variant="admin"
              ref={captionInputRef}
              value={captionText}
              onChange={(e) => setCaptionText(e.target.value)}
              onBlur={(e) => {
                // Only trigger blur if we're not clicking on the Turn Off button
                if (!e.relatedTarget || !e.relatedTarget.textContent?.includes('Turn Off')) {
                  handleCaptionBlur()
                }
              }}
              onKeyDown={handleCaptionKeyDown}
              placeholder="Enter image caption... (Press Enter to apply, Escape to cancel)"
              className="flex-1 px-3 py-2 border border-admin_bordered rounded-md focus:outline-none focus:ring-2 focus:ring-admin_primary focus:border-transparent"
            />
          </div>
        </div>
      )}

      {/* Alt Text Panel */}
      {altTextPanelOpen && (
        <div className="absolute top-full left-0 mt-2 p-3 bg-admin_paper border border-admin_bordered rounded-lg shadow-lg z-50 min-w-[300px]">
          <Typography 
            variant="admin_label"
            className="mt-0 mb-2"
          >
            Edit Alt Text
          </Typography>

          <Input
            variant="admin"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            placeholder="Describe the image..."
            className="w-full mb-3 px-3 py-2 border border-admin_bordered rounded-md focus:outline-none focus:ring-2 focus:ring-admin_primary focus:border-transparent"
          />

          <div className="flex gap-2">
            <Button 
              variant="admin_primary" 
              onClick={handleAltTextSave}
            >
              Apply
            </Button>
            <Button 
              variant="admin_secondary" 
              onClick={() => setAltTextPanelOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// Store reference to current click outside handler
let currentClickOutsideHandler = null

const removeExistingMenu = () => {
  // Remove the previous click outside handler first
  if (currentClickOutsideHandler) {
    document.removeEventListener("mousedown", currentClickOutsideHandler)
    currentClickOutsideHandler = null
  }

  const existingMenu = document.querySelector(".ae-horizontal-options-menu-container")
  if (existingMenu) {
    // Force cleanup of any tooltips before removing the menu by triggering mouseleave on all tooltip triggers
    const tooltipTriggers = existingMenu.querySelectorAll('button, [role="button"]')
    tooltipTriggers.forEach(trigger => {
      // Trigger mouseleave to hide any active tooltips
      trigger.dispatchEvent(new MouseEvent('mouseleave', {
        view: window,
        bubbles: true,
        cancelable: true
      }))
    })
    
    // Remove menu immediately (no delay needed since event listener is already removed)
    existingMenu.remove()
  } else {
    // If no menu container, still try to clean up any orphaned tooltips
    const allTooltips = document.querySelectorAll('[class*="bg-admin_canvas"][class*="text-admin_text"][class*="fixed"]')
    allTooltips.forEach(tooltip => {
      tooltip.remove()
    })
  }
}

// Wrapper component that handles positioning
const PositionedHorizontalMenu = ({ selectedImage, onImageUpdated, onClose, variables }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const menuRef = useRef(null)
  
  useEffect(() => {
    if (!selectedImage) return

    const updatePosition = () => {
      const imageRect = selectedImage.getBoundingClientRect()
      const scrollY = window.pageYOffset || document.documentElement.scrollTop
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft
      
      // Calculate the center of the image
      const imageCenterX = imageRect.left + scrollX + (imageRect.width / 2)
      
      // Get menu width to center it properly
      let menuWidth = 0
      if (menuRef.current) {
        menuWidth = menuRef.current.getBoundingClientRect().width
      }
      
      // Get menu height to position it properly above the image
      let menuHeight = 0
      if (menuRef.current) {
        menuHeight = menuRef.current.getBoundingClientRect().height
      }

      setPosition({
        top: imageRect.top + scrollY - menuHeight - 10, // Position above image with 10px gap
        left: imageCenterX - (menuWidth / 2)
      })
    }

    // Initial position
    updatePosition()
    
    // Update position again after menu renders to get accurate width
    setTimeout(updatePosition, 0)

    // Update position on scroll, resize, or DOM changes
    window.addEventListener('scroll', updatePosition)
    window.addEventListener('resize', updatePosition)
    
    const observer = new MutationObserver(updatePosition)
    if (selectedImage.parentElement) {
      observer.observe(selectedImage.parentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      })
    }

    // Watch for menu size changes
    let resizeObserver = null
    if (menuRef.current) {
      resizeObserver = new ResizeObserver(updatePosition)
      resizeObserver.observe(menuRef.current)
    }

    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
      observer.disconnect()
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [selectedImage])

  return (
    <div 
      ref={menuRef}
      className="ae-horizontal-options-menu fixed"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: (modalStack.getTop()?.zIndex ?? 0) + 10,
      }}
    >
      <HorizontalImageOptionsMenu
        selectedImage={selectedImage}
        onImageUpdated={onImageUpdated}
        onClose={onClose}
        variables={variables}
      />
    </div>
  )
}

const HorizontalOptionsMenu = (data) => {
  const { selectedImage, onImageUpdated, variables } =
    typeof data === "object" ? data : { selectedImage: data }

  if (!selectedImage) return
  removeExistingMenu()

  // Create container
  const menuContainer = document.createElement("div")
  menuContainer.className = "ae-horizontal-options-menu-container"
  document.body.appendChild(menuContainer)

  // Store the current selected image reference that can be updated
  let currentSelectedImage = selectedImage

  const handleClickOutside = (e) => {
    // Check if click is inside the menu container
    if (menuContainer.contains(e.target)) return
    
    // Check if click is on the selected image (using more robust detection)
    if (e.target === currentSelectedImage) return
    
    // Also check if click is on an image with the same data-image-id (in case image was moved)
    if (e.target.tagName === "IMG" && currentSelectedImage.tagName === "IMG") {
      const clickedImageId = e.target.getAttribute('data-image-id')
      const selectedImageId = currentSelectedImage.getAttribute('data-image-id')
      if (clickedImageId && selectedImageId && clickedImageId === selectedImageId) {
        // Update the reference to the new image element
        currentSelectedImage = e.target
        return
      }
    }
    
    // Check if click is inside the figure container of the selected image
    const selectedFigure = currentSelectedImage.closest('figure')
    if (selectedFigure && selectedFigure.contains(e.target)) return
    
    // Check if click is inside any DropdownSelect panel (they use portals)
    const dropdownPanels = document.querySelectorAll('[role="menu"], .ae-dropdown-panel')
    for (let panel of dropdownPanels) {
      if (panel.contains(e.target)) return
    }
    
    // Check if click is inside any tooltip
    const tooltips = document.querySelectorAll('[class*="tooltip"], [data-tooltip]')
    for (let tooltip of tooltips) {
      if (tooltip.contains(e.target)) return
    }
    
    // If we get here, it's a click outside - close the menu
    removeExistingMenu()
  }

  // Store the current handler reference and add event listener
  currentClickOutsideHandler = handleClickOutside
  document.addEventListener("mousedown", handleClickOutside)

  // Create a wrapper for onImageUpdated that updates our reference
  const wrappedOnImageUpdated = (img) => {
    if (img && img !== currentSelectedImage) {
      // Update our reference to the new image
      currentSelectedImage = img
    }
    if (onImageUpdated) {
      onImageUpdated(img)
    }
  }

  const root = createRoot(menuContainer)
  root.render(
    <PositionedHorizontalMenu
      selectedImage={currentSelectedImage}
      onImageUpdated={wrappedOnImageUpdated}
      onClose={() => {
        removeExistingMenu()
      }}
      variables={variables}
    />
  )
}

// Alt Text Editor Component
const AltTextEditor = ({ selectedImage, onClose }) => {
  const [altText, setAltText] = useState(selectedImage?.alt || "")
  const inputRef = useRef(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const handleSave = () => {
    if (selectedImage) {
      selectedImage.alt = altText
    }
    onClose()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  return (
    <div className="bg-admin_paper border border-admin_bordered rounded-lg shadow-lg p-4 min-w-[300px]">
      <Typography 
        variant="admin_label"
        className="mt-0 mb-2"
      >
        Edit Alt Text
      </Typography>

      <Input
        variant="admin"
        ref={inputRef}
        value={altText}
        onChange={(e) => setAltText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Describe the image..."
        className="w-full mb-3"
      />
      <div className="flex gap-2 justify-end">
        <Button
          variant="admin_secondary"
          onClick={onClose}
        >
          Cancel
        </Button>

        <Button
          variant="admin_primary"
          onClick={handleSave}
        >
          Apply
        </Button>
      </div>
    </div>
  )
}

// Function to show alt text editor in same position as menu
const showAltTextEditor = (selectedImage) => {
  if (!selectedImage) return
  removeExistingMenu() // Remove any existing menus first

  // Create container
  const editorContainer = document.createElement("div")
  editorContainer.className = "ae-alt-text-editor-container"
  document.body.appendChild(editorContainer)

  // Position the editor at the same location as the menu would be
  const imageRect = selectedImage.getBoundingClientRect()
  const scrollY = window.pageYOffset || document.documentElement.scrollTop
  const scrollX = window.pageXOffset || document.documentElement.scrollLeft

  const imageCenterX = imageRect.left + scrollX + (imageRect.width / 2)

  const handleClickOutside = (e) => {
    if (editorContainer.contains(e.target)) return
    if (e.target === selectedImage) return

    // Close editor
    removeEditor()
  }

  const removeEditor = () => {
    document.removeEventListener("mousedown", handleClickOutside)
    if (editorContainer) {
      editorContainer.remove()
    }
  }

  // Add click outside handler
  document.addEventListener("mousedown", handleClickOutside)

  const root = createRoot(editorContainer)
  root.render(
    <div
      className="fixed"
      style={{
        top: `${imageRect.top + scrollY - 120}px`, // Position above image
        left: `${imageCenterX - 150}px`, // Center horizontally (300px width / 2)
        zIndex: (modalStack.getTop()?.zIndex ?? 0) + 10,
      }}
    >
      <AltTextEditor
        selectedImage={selectedImage}
        onClose={() => {
          removeEditor()
        }}
      />
    </div>
  )
}

export default HorizontalOptionsMenu